import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { html } from 'lit/static-html.js';

import type { Group } from '../../../core/group-by/trait.js';
import { dataViewI18nT, renderUniLit } from '../../../core/index.js';
import { dragHandler } from '../../../core/utils/wc-dnd/dnd-context.js';
import { sortable } from '../../../core/utils/wc-dnd/sort/sort-context.js';
import { resolveKanbanVisibleGroups } from '../card-render-window.js';
import type { KanbanViewUILogic } from './kanban-view-ui-logic.js';
import { kanbanRowSortId } from './layout.js';

const styles = css`
  affine-data-view-kanban-row {
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid var(--streamlain-border-color);
    padding-bottom: 8px;
    margin-bottom: 8px;
  }

  affine-data-view-kanban-row:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .row-header {
    position: sticky;
    top: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    min-height: 36px;
    padding: 7px 8px;
    overflow: clip;
    font-family: var(--streamlain-font-family);
    font-size: var(--data-view-cell-text-size);
    font-weight: 600;
    color: var(--streamlain-text-primary-color);
    background: var(--streamlain-background-secondary-color);
    border-radius: 8px;
    user-select: none;
  }

  .row-header-toggle {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: 0;
    color: inherit;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: grab;
  }

  .row-header-toggle:active {
    cursor: grabbing;
  }

  .row-header[data-sort-disabled='true'] .row-header-toggle {
    cursor: not-allowed;
  }

  .row-header[data-readonly='true'] .row-header-toggle {
    cursor: pointer;
  }

  .row-header:hover {
    background: var(--streamlain-hover-color);
  }

  .row-header-chevron {
    display: inline-flex;
    width: 16px;
    height: 16px;
    align-items: center;
    justify-content: center;
    transition: transform 120ms ease;
    color: var(--streamlain-icon-color);
  }

  .row-header[data-collapsed='true'] .row-header-chevron {
    transform: rotate(-90deg);
  }

  .row-header-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .row-header-count {
    color: var(--streamlain-text-secondary-color);
    font-weight: 400;
    font-size: 12px;
  }

  .row-header-ops {
    display: inline-flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 120ms;
  }

  .row-header:hover .row-header-ops {
    opacity: 1;
  }

  .row-header-add {
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
  }

  .row-header-add:hover {
    background: var(--streamlain-black-10);
  }

  .row-body-scroll {
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    display: grid;
    grid-template-rows: 1fr;
    transition:
      grid-template-rows 180ms ease,
      opacity 180ms ease;
    opacity: 1;
  }

  .row-body-scroll[data-collapsed='true'] {
    grid-template-rows: 0fr;
    opacity: 0;
    overflow: hidden;
  }

  .row-body {
    display: flex;
    gap: 20px;
    padding-top: 8px;
    width: max-content;
    min-width: 100%;
    overflow: visible;
    min-height: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    .row-body-scroll {
      transition: none;
    }
  }

  .row-group-overflow {
    width: var(--kanban-column-width, 260px);
    flex-shrink: 0;
    border: 1px solid var(--streamlain-border-color);
    border-radius: 8px;
    color: var(--streamlain-text-secondary-color);
    padding: 12px;
    text-align: center;
    cursor: pointer;
    user-select: none;
  }

  .row-group-overflow:hover {
    background: var(--streamlain-hover-color);
    color: var(--streamlain-text-primary-color);
  }
`;

const ChevronIcon = () =>
  html`<svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
    <path d="M4 6l4 4 4-4" />
  </svg>`;

export class KanbanRow extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = styles;

  private get view() {
    return this.kanbanViewLogic.view;
  }

  private get collapsed(): boolean {
    return this.view.isRow2Collapsed(this.row.key);
  }

  private readonly toggleCollapse = (e: MouseEvent) => {
    if (
      (e.target as HTMLElement).closest(
        '.row-header-add, .row-header-ops button'
      )
    ) {
      return;
    }
    this.view.toggleRow2Collapsed(this.row.key);
    this.requestUpdate();
  };

  private readonly clickAddCardInRow = (e: MouseEvent) => {
    e.stopPropagation();
    const firstCol = this.kanbanViewLogic.groups$.value?.[0];
    if (!firstCol) return;
    const id = this.view.addCard('end', firstCol.key, this.row.key);
    requestAnimationFrame(() => {
      const columnId =
        this.view.mainProperties$.value.titleColumn ||
        this.view.propertyIds$.value[0];
      if (!columnId) return;
      this.kanbanViewLogic.selectionController.selection = {
        selectionType: 'cell',
        groupKey: firstCol.key,
        rowKey: this.row.key,
        cardId: id,
        columnId,
        isEditing: true,
      };
    });
    this.requestUpdate();
  };

  private readonly clickRowOps = (e: MouseEvent) => {
    const ele = e.currentTarget as HTMLElement;
    const group2Trait = this.kanbanViewLogic.group2Trait$.value;
    const rows = group2Trait?.groupsDataList$.value ?? [];
    const rowIndex = rows.findIndex(row => row?.key === this.row.key);
    const canReorder =
      !this.view.readonly$.value &&
      !(group2Trait?.isExplicitSort$.value ?? false);
    popFilterableSimpleMenu(popupTargetFromElement(ele), [
      menu.action({
        name: dataViewI18nT('kanban.moveRowGroupUp'),
        hide: () => !canReorder || rowIndex <= 0,
        select: () => {
          const previous = rows[rowIndex - 1];
          if (previous) {
            group2Trait?.moveGroupTo(this.row.key, {
              before: true,
              id: previous.key,
            });
          }
        },
      }),
      menu.action({
        name: dataViewI18nT('kanban.moveRowGroupDown'),
        hide: () => !canReorder || rowIndex < 0 || rowIndex >= rows.length - 1,
        select: () => {
          const next = rows[rowIndex + 1];
          if (next) {
            group2Trait?.moveGroupTo(this.row.key, {
              before: false,
              id: next.key,
            });
          }
        },
      }),
      menu.action({
        name: this.collapsed ? 'Expand' : 'Collapse',
        select: () => {
          this.view.toggleRow2Collapsed(this.row.key);
          this.requestUpdate();
        },
      }),
      menu.action({
        name: 'Delete Cards in Row',
        hide: () => this.row.rows.length === 0,
        select: () => {
          this.view.rowsDelete(this.row.rows.map(r => r.rowId));
          this.requestUpdate();
        },
      }),
    ]);
  };

  override render() {
    const collapsed = this.collapsed;
    const sortDisabled =
      this.kanbanViewLogic.group2Trait$.value?.isExplicitSort$.value ?? false;
    const readonly = this.view.readonly$.value;
    const cardCount = this.row.rows.length;
    const cols = this.kanbanViewLogic.groups$.value ?? [];
    const { visibleGroups: visibleCols, hiddenCount } =
      resolveKanbanVisibleGroups(
        cols.filter((col): col is NonNullable<typeof col> => col != null),
        this.kanbanViewLogic.columnRenderLimit$.value
      );
    return html`
      <div
        class="row-header"
        data-collapsed="${collapsed ? 'true' : 'false'}"
        data-row-key="${this.row.key}"
        data-sort-disabled="${sortDisabled ? 'true' : 'false'}"
        data-readonly="${readonly ? 'true' : 'false'}"
        title="${readonly
          ? ''
          : sortDisabled
            ? dataViewI18nT('kanban.reorderRequiresDefaultSort')
            : dataViewI18nT('kanban.reorderRowGroup')}"
      >
        <button
          class="row-header-toggle"
          ${readonly ? nothing : dragHandler(kanbanRowSortId(this.row.key))}
          aria-expanded="${collapsed ? 'false' : 'true'}"
          @click="${this.toggleCollapse}"
        >
          <span class="row-header-chevron">${ChevronIcon()}</span>
          <span class="row-header-title">
            ${renderUniLit(
              (
                this.row as unknown as {
                  manager?: {
                    groupInfo$: { value?: { config?: { view?: unknown } } };
                  };
                }
              ).manager?.groupInfo$.value?.config?.view as never,
              {
                group: this.row,
                readonly: this.view.readonly$.value,
              }
            ) || html`<span>${this.row.name$.value || 'Unassigned'}</span>`}
            <span class="row-header-count">${cardCount}</span>
          </span>
        </button>
        <div class="row-header-ops">
          ${readonly
            ? nothing
            : html`<button
                class="row-header-add"
                @click="${this.clickAddCardInRow}"
              >
                <span
                  style="display:inline-flex;width:14px;height:14px;align-items:center;"
                  >${AddCursorIcon()}</span
                >
                Add
              </button>`}
          <button @click="${this.clickRowOps}">⋮</button>
        </div>
      </div>
      <div
        class="row-body-scroll"
        data-collapsed="${collapsed ? 'true' : 'false'}"
        data-row-key="${this.row.key}"
        aria-hidden="${collapsed ? 'true' : 'false'}"
        ?inert="${collapsed}"
      >
        <div
          class="row-body"
          data-collapsed="${collapsed ? 'true' : 'false'}"
          data-row-key="${this.row.key}"
        >
          ${repeat(
            visibleCols,
            col => col?.key ?? 'default',
            col => {
              return html`<affine-data-view-kanban-group
                ${sortable(col.key)}
                data-key="${col.key}"
                data-row-key="${this.row.key}"
                .rowKey="${this.row.key}"
                .kanbanViewLogic="${this.kanbanViewLogic}"
                .group="${col}"
              ></affine-data-view-kanban-group>`;
            }
          )}
          ${hiddenCount > 0
            ? html`<div
                class="row-group-overflow"
                role="button"
                title="${dataViewI18nT('kanban.showMoreColumnsTitle')}"
                data-hidden-kanban-group-count="${hiddenCount}"
                @click="${this.kanbanViewLogic.showMoreColumns}"
              >
                ${dataViewI18nT('kanban.showMoreColumns', {
                  count: hiddenCount,
                })}
              </div>`
            : nothing}
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor row!: Group;

  @property({ attribute: false })
  accessor kanbanViewLogic!: KanbanViewUILogic;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-kanban-row': KanbanRow;
  }
}
